/**
 * Servicio de Geocodificación del Catastro de España
 * 
 * Permite geocodificar referencias catastrales a coordenadas UTM30 ETRS89.
 * Usa los servicios web libres de la Sede Electrónica del Catastro (OVC).
 * 
 * Casos de uso PTEL:
 * - "Parcela 45, Polígono 3, Colomera" → Coordenadas UTM30
 * - "18090A003000450000XY" → Coordenadas UTM30
 * - Validación de parcelas rústicas en documentos de emergencias
 * 
 * Endpoints utilizados:
 * - Consulta_CPMRC: Referencia catastral → Coordenadas
 * - INSPIRE WFS: Geometría de parcelas (fallback)
 * 
 * @module services/geocoding/generic
 * @see https://www.catastro.hacienda.gob.es/ws/Webservices_Libres.pdf
 */

import type { AxiosInstance } from 'axios';
import axios from 'axios';
import type { GeocodingResult } from '../../../types/infrastructure';
import { buscarPorNombre } from '../../../lib/CodigoMunicipalResolver';

/**
 * Resultado interno del servicio Catastro.
 *
 * TD-010: desacoplado de `GeocodingSource` canónico porque el servicio
 * produce resultados «crudos» de OVC/WFS INSPIRE, y son los consumidores
 * (CatastroGeocoder, GeocodingOrchestrator) los que asignan la fuente
 * canónica (`'catastro_ptel'` o `'catastro_api'`) según su contexto.
 *
 * El campo opcional `sourceHint` permite al servicio transmitir qué
 * endpoint concreto de Catastro produjo el resultado, sin comprometer
 * al consumidor a un valor específico de `GeocodingSource`.
 */
export type CatastroServiceResult = Omit<GeocodingResult, 'source'> & {
  sourceHint?: 'ovc_rest' | 'inspire_wfs';
};

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

/**
 * Referencia catastral parseada
 */
export interface ParsedCadastralRef {
  /** Código de provincia (2 dígitos) */
  province: string;
  /** Código de municipio (3 dígitos Hacienda + 2 control) */
  municipality: string;
  /** Sector/Agregado (1 carácter: A=rústica, otros=urbana) */
  sector: string;
  /** Polígono (3 dígitos) */
  polygon: string;
  /** Parcela (5 dígitos) */
  parcel: string;
  /** Referencia completa de 14 caracteres */
  fullRef: string;
  /** ¿Es parcela rústica? */
  isRural: boolean;
}

/**
 * Opciones para búsqueda catastral
 */
export interface CatastroSearchOptions {
  /** Referencia catastral completa (14-20 caracteres) */
  cadastralRef?: string;
  /** Número de polígono (para parcelas rústicas) */
  polygon?: string | number;
  /** Número de parcela */
  parcel?: string | number;
  /** Nombre del municipio */
  municipality: string;
  /** Nombre de la provincia */
  province: string;
  /** Sistema de referencia deseado (defecto: EPSG:25830) */
  srs?: string;
}

/**
 * Respuesta del servicio de coordenadas del Catastro
 */
interface CatastroCoordResponse {
  /** Coordenada X del centroide */
  xcen: number;
  /** Coordenada Y del centroide */
  ycen: number;
  /** Sistema de referencia */
  srs: string;
  /** Dirección o localización textual */
  address: string;
  /** Referencia catastral confirmada */
  refCat: string;
}

/**
 * Estadísticas del servicio
 */
interface CatastroStats {
  totalRequests: number;
  successfulRequests: number;
  failedRequests: number;
  cacheHits: number;
  avgResponseTime: number;
}

// ============================================================================
// CONSTANTES
// ============================================================================

/** Códigos de provincia de Andalucía */
const ANDALUCIA_PROVINCES: Record<string, string> = {
  'ALMERIA': '04', 'ALMERÍA': '04',
  'CADIZ': '11', 'CÁDIZ': '11',
  'CORDOBA': '14', 'CÓRDOBA': '14',
  'GRANADA': '18',
  'HUELVA': '21',
  'JAEN': '23', 'JAÉN': '23',
  'MALAGA': '29', 'MÁLAGA': '29',
  'SEVILLA': '41'
};

// ============================================================================
// SERVICIO PRINCIPAL
// ============================================================================

/**
 * Geocodificador del Catastro de España
 * 
 * @example
 * ```typescript
 * const catastro = new CatastroService();
 * 
 * // Por referencia catastral completa
 * const result1 = await catastro.geocode({
 *   cadastralRef: '18090A003000450000XY',
 *   municipality: 'Colomera',
 *   province: 'Granada'
 * });
 * 
 * // Por polígono y parcela
 * const result2 = await catastro.geocode({
 *   polygon: 3,
 *   parcel: 45,
 *   municipality: 'Colomera',
 *   province: 'Granada'
 * });
 * ```
 */
export class CatastroService {
  // Endpoint principal de coordenadas
  private readonly COORD_ENDPOINT = 
    'https://ovc.catastro.meh.es/ovcservweb/OVCSWLocalizacionRC/OVCCoordenadas.asmx/Consulta_CPMRC';
  
  // Endpoint INSPIRE WFS (fallback con geometría completa)
  private readonly WFS_ENDPOINT = 
    'https://ovc.catastro.meh.es/INSPIRE/wfsCP.aspx';
  
  // Endpoint de callejero para resolver municipios
  private readonly CALLEJERO_ENDPOINT = 
    'https://ovc.catastro.meh.es/ovcservweb/OVCSWLocalizacionRC/OVCCallejero.asmx';

  private axiosInstance: AxiosInstance;
  private cache: Map<string, CatastroServiceResult>;
  private stats: CatastroStats;

  constructor() {
    this.axiosInstance = axios.create({
      timeout: 15000,
      headers: {
        'Accept': 'application/xml, text/xml',
      }
    });

    this.cache = new Map();
    this.stats = {
      totalRequests: 0,
      successfulRequests: 0,
      failedRequests: 0,
      cacheHits: 0,
      avgResponseTime: 0
    };
  }

  /**
   * Geocodifica una referencia catastral
   */
  public async geocode(options: CatastroSearchOptions): Promise<CatastroServiceResult | null> {
    const startTime = Date.now();
    this.stats.totalRequests++;

    // Construir o validar referencia catastral
    let cadastralRef = options.cadastralRef;
    
    if (!cadastralRef && options.polygon && options.parcel) {
      // Construir referencia desde polígono/parcela
      cadastralRef = this.buildCadastralRef(options) ?? undefined;
    }

    if (!cadastralRef) {
      console.warn('[CatastroService] No se pudo determinar referencia catastral');
      this.stats.failedRequests++;
      return null;
    }

    // Normalizar referencia (solo primeros 14 caracteres para parcela)
    const refNormalized = cadastralRef.substring(0, 14).toUpperCase();
    
    // Verificar caché
    const cacheKey = this.buildCacheKey(refNormalized, options.municipality);
    if (this.cache.has(cacheKey)) {
      this.stats.cacheHits++;
      return this.cache.get(cacheKey) || null;
    }

    try {
      // Intentar servicio de coordenadas primero
      let result = await this.geocodeViaCoordService(
        refNormalized, 
        options.municipality, 
        options.province,
        options.srs || 'EPSG:25830'
      );

      // Si falla, intentar WFS INSPIRE
      if (!result) {
        result = await this.geocodeViaWFS(refNormalized, options.srs || 'EPSG:25830');
      }

      if (result) {
        this.cache.set(cacheKey, result);
        this.stats.successfulRequests++;
      } else {
        this.stats.failedRequests++;
      }

      // Actualizar tiempo promedio
      const responseTime = Date.now() - startTime;
      this.updateAvgResponseTime(responseTime);

      return result;

    } catch (error) {
      console.error('[CatastroService] Error geocodificando:', error);
      this.stats.failedRequests++;
      return null;
    }
  }

  /**
   * Geocodifica mediante el servicio de coordenadas (Consulta_CPMRC)
   */
  private async geocodeViaCoordService(
    cadastralRef: string,
    municipality: string,
    province: string,
    srs: string
  ): Promise<CatastroServiceResult | null> {
    try {
      const params = new URLSearchParams({
        Provincia: province,
        Municipio: municipality,
        SRS: srs,
        RC: cadastralRef
      });

      const response = await this.axiosInstance.get(
        `${this.COORD_ENDPOINT}?${params.toString()}`
      );

      const parsed = this.parseCoordResponse(response.data);
      
      if (!parsed) {
        return null;
      }

      return this.buildGeocodingResult(parsed, cadastralRef, municipality);

    } catch (error) {
      console.warn('[CatastroService] Coord service failed:', error);
      return null;
    }
  }

  /**
   * Geocodifica mediante WFS INSPIRE (fallback)
   */
  private async geocodeViaWFS(
    cadastralRef: string,
    srs: string
  ): Promise<CatastroServiceResult | null> {
    try {
      const params = new URLSearchParams({
        service: 'wfs',
        version: '2',
        request: 'getfeature',
        STOREDQUERIE_ID: 'GetParcel',
        srsname: srs,
        REFCAT: cadastralRef
      });

      const response = await this.axiosInstance.get(
        `${this.WFS_ENDPOINT}?${params.toString()}`
      );

      return this.parseWFSResponse(response.data, cadastralRef);

    } catch (error) {
      console.warn('[CatastroService] WFS failed:', error);
      return null;
    }
  }

  /**
   * Parsea respuesta XML del servicio de coordenadas
   */
  private parseCoordResponse(xml: string): CatastroCoordResponse | null {
    try {
      // Extraer coordenadas del XML
      const xcenMatch = xml.match(/<xcen>([^<]+)<\/xcen>/i);
      const ycenMatch = xml.match(/<ycen>([^<]+)<\/ycen>/i);
      const srsMatch = xml.match(/<srs>([^<]+)<\/srs>/i);
      const ldtMatch = xml.match(/<ldt>([^<]+)<\/ldt>/i);
      const pc1Match = xml.match(/<pc1>([^<]+)<\/pc1>/i);
      const pc2Match = xml.match(/<pc2>([^<]+)<\/pc2>/i);

      // Verificar errores
      const errorMatch = xml.match(/<err>([^<]+)<\/err>/i);
      if (errorMatch) {
        console.warn('[CatastroService] Error del servicio:', errorMatch[1]);
        return null;
      }

      if (!xcenMatch || !ycenMatch) {
        return null;
      }

      const xcen = parseFloat(xcenMatch[1]);
      const ycen = parseFloat(ycenMatch[1]);

      if (isNaN(xcen) || isNaN(ycen)) {
        return null;
      }

      return {
        xcen,
        ycen,
        srs: srsMatch?.[1] || 'EPSG:25830',
        address: ldtMatch?.[1] || '',
        refCat: (pc1Match?.[1] || '') + (pc2Match?.[1] || '')
      };

    } catch (error) {
      console.error('[CatastroService] Error parseando XML:', error);
      return null;
    }
  }

  /**
   * Parsea respuesta WFS INSPIRE (GML)
   */
  private parseWFSResponse(xml: string, cadastralRef: string): CatastroServiceResult | null {
    try {
      // Buscar geometría Point o centroide
      const posMatch = xml.match(/<gml:pos[^>]*>([^<]+)<\/gml:pos>/i);
      
      if (!posMatch) {
        // Intentar formato posList (polígono - calcular centroide)
        const posListMatch = xml.match(/<gml:posList[^>]*>([^<]+)<\/gml:posList>/i);
        if (posListMatch) {
          return this.calculateCentroidFromPosList(posListMatch[1], cadastralRef);
        }
        return null;
      }

      const coords = posMatch[1].trim().split(/\s+/);
      if (coords.length < 2) return null;

      const x = parseFloat(coords[0]);
      const y = parseFloat(coords[1]);

      if (isNaN(x) || isNaN(y)) return null;

      return {
        x,
        y,
        confidence: 85, // Confianza algo menor por ser WFS
        sourceLayer: 'CP.CadastralParcel',
        matchedName: cadastralRef,
        matchType: 'cadastral',
        metadata: {
          service: 'INSPIRE_WFS',
          refCat: cadastralRef
        }
      };

    } catch (error) {
      console.error('[CatastroService] Error parseando WFS:', error);
      return null;
    }
  }

  /**
   * Calcula centroide a partir de lista de coordenadas de polígono
   */
  private calculateCentroidFromPosList(
    posList: string, 
    cadastralRef: string
  ): CatastroServiceResult | null {
    try {
      const values = posList.trim().split(/\s+/).map(parseFloat);
      if (values.length < 4 || values.some(isNaN)) return null;

      let sumX = 0, sumY = 0, count = 0;
      
      for (let i = 0; i < values.length - 1; i += 2) {
        sumX += values[i];
        sumY += values[i + 1];
        count++;
      }

      if (count === 0) return null;

      return {
        x: sumX / count,
        y: sumY / count,
        confidence: 80, // Centroide calculado = menor confianza
        sourceLayer: 'CP.CadastralParcel',
        matchedName: cadastralRef,
        matchType: 'cadastral_centroid',
        metadata: {
          service: 'INSPIRE_WFS',
          refCat: cadastralRef,
          vertexCount: count
        }
      };

    } catch {
      return null;
    }
  }

  /**
   * Construye resultado de geocodificación
   */
  private buildGeocodingResult(
    coord: CatastroCoordResponse,
    cadastralRef: string,
    municipality: string
  ): CatastroServiceResult {
    // Determinar si es rústica (sector A)
    const isRural = cadastralRef.length >= 6 && cadastralRef[5] === 'A';

    return {
      x: coord.xcen,
      y: coord.ycen,
      confidence: 92, // Alta confianza - datos oficiales del Catastro
      sourceLayer: 'OVCCoordenadas',
      matchedName: coord.address || cadastralRef,
      matchType: isRural ? 'cadastral_rural' : 'cadastral_urban',
      address: coord.address,
      municipality: municipality,
      metadata: {
        refCat: coord.refCat || cadastralRef,
        srs: coord.srs,
        isRural
      }
    };
  }

  /**
   * Construye referencia catastral desde polígono/parcela
   * Formato rústica: PPMMM A PPP PPPPP (14 chars)
   * - PP: Provincia (2)
   * - MMM: Municipio Catastro (3) - ¡Diferente de INE!
   * - A: Sector rústico (1)
   * - PPP: Polígono (3)
   * - PPPPP: Parcela (5)
   * 
   * IMPORTANTE: Usa CodigoMunicipalResolver para convertir nombre → código Catastro
   * porque INE ≠ Catastro en muchos casos (offset Granada, capitales, etc.)
   */
  private buildCadastralRef(options: CatastroSearchOptions): string | null {
    const { polygon, parcel, province, municipality } = options;
    
    if (!polygon || !parcel || !municipality) return null;

    // Obtener código de provincia
    const provCode = this.getProvinceCode(province);
    if (!provCode) {
      console.warn('[CatastroService] Provincia no reconocida:', province);
      return null;
    }

    // Resolver código Catastro del municipio usando CodigoMunicipalResolver
    const resultado = buscarPorNombre(municipality);
    if (!resultado.encontrado || !resultado.municipio) {
      console.warn('[CatastroService] Municipio no encontrado en equivalencias INE-Catastro:', municipality);
      return null;
    }

    // Usar código Catastro (NO INE) - importante para Granada (+1), capitales (900), etc.
    const codCatastro = resultado.municipio.codCatastro.toString().padStart(3, '0');
    const polyStr = String(polygon).padStart(3, '0');
    const parcelStr = String(parcel).padStart(5, '0');

    // Formato: PPMMM A PPP PPPPP (A = rústica)
    return `${provCode}${codCatastro}A${polyStr}${parcelStr}`;
  }

  /**
   * Obtiene código de provincia andaluza
   */
  private getProvinceCode(province: string): string | null {
    const normalized = province
      .toUpperCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    
    return ANDALUCIA_PROVINCES[normalized] || null;
  }

  /**
   * Construye clave de caché
   */
  private buildCacheKey(cadastralRef: string, municipality: string): string {
    return `${cadastralRef}|${municipality.toLowerCase()}`;
  }

  /**
   * Actualiza tiempo promedio de respuesta
   */
  private updateAvgResponseTime(newTime: number): void {
    const total = this.stats.totalRequests;
    this.stats.avgResponseTime = 
      (this.stats.avgResponseTime * (total - 1) + newTime) / total;
  }

  // ============================================================================
  // MÉTODOS PÚBLICOS DE UTILIDAD
  // ============================================================================

  /**
   * Parsea una cadena de texto para extraer referencia catastral
   * 
   * Soporta múltiples formatos encontrados en documentos PTEL:
   * - Referencia completa: "18090A00300045"
   * - Formal: "Polígono 3, Parcela 45"
   * - Abreviado: "P.3, Pa.45" o "P-3, Pa-45"
   * - Numérico: "3/45" o "3-45"
   * - Narrativo: "Finca Rústica en Polígono 3 Parcela 45 del término de Colomera"
   * - Con contexto: "Parcela catastral 18090A00300045 (Granada)"
   * - Híbrido: "P-3/P-45" o "POL3/PAR45"
   * 
   * @example
   * ```typescript
   * const ref = catastro.parseCadastralText('Parcela 45, Polígono 3');
   * // { polygon: '003', parcel: '00045' }
   * 
   * const ref2 = catastro.parseCadastralText('18090A003000450000XY');
   * // { fullRef: '18090A00300045', province: '18', ... }
   * 
   * const ref3 = catastro.parseCadastralText('Finca en Polígono 5 Parcela 123 de Colomera');
   * // { polygon: '005', parcel: '00123' }
   * ```
   */
  public parseCadastralText(text: string): Partial<ParsedCadastralRef> | null {
    if (!text || typeof text !== 'string') return null;

    const normalized = text.toUpperCase().trim();

    // Patrón 1: Referencia catastral completa (14-20 chars alfanuméricos)
    // Coincidencia exacta al inicio/fin de cadena
    const fullRefMatch = normalized.match(/^([0-9]{5}[A-Z][0-9]{3}[0-9A-Z]{5})([0-9A-Z]{0,6})?$/);
    if (fullRefMatch) {
      const ref = fullRefMatch[1];
      return {
        fullRef: ref,
        province: ref.substring(0, 2),
        municipality: ref.substring(2, 5),
        sector: ref.substring(5, 6),
        polygon: ref.substring(6, 9),
        parcel: ref.substring(9, 14),
        isRural: ref[5] === 'A'
      };
    }

    // Patrón 2: Referencia catastral EMBEBIDA en texto con contexto
    // Ej: "Parcela catastral 18090A00300045 (Granada)"
    // Ej: "Ref. Cat. 18090A00300045 - Colomera"
    const embeddedRefMatch = normalized.match(
      /(?:PARCELA\s+CATASTRAL|REF(?:\.|ERENCIA)?\s*CAT(?:\.|ASTRAL)?|RC)\s*:?\s*([0-9]{5}[A-Z][0-9]{3}[0-9A-Z]{5})/
    );
    if (embeddedRefMatch) {
      const ref = embeddedRefMatch[1];
      return {
        fullRef: ref,
        province: ref.substring(0, 2),
        municipality: ref.substring(2, 5),
        sector: ref.substring(5, 6),
        polygon: ref.substring(6, 9),
        parcel: ref.substring(9, 14),
        isRural: ref[5] === 'A'
      };
    }

    // Patrón 3: "Polígono X, Parcela Y" o "Parcela Y, Polígono X" (formal)
    const polyParcelMatch = normalized.match(
      /POL[IÍ]GONO\s*:?\s*(\d+)[,\s]+PARCELA\s*:?\s*(\d+)|PARCELA\s*:?\s*(\d+)[,\s]+POL[IÍ]GONO\s*:?\s*(\d+)/i
    );
    if (polyParcelMatch) {
      const polygon = polyParcelMatch[1] || polyParcelMatch[4];
      const parcel = polyParcelMatch[2] || polyParcelMatch[3];
      return {
        polygon: polygon.padStart(3, '0'),
        parcel: parcel.padStart(5, '0'),
        isRural: true
      };
    }

    // Patrón 4: Texto NARRATIVO con "en" o "del término de"
    // Ej: "Finca Rústica en Polígono 3 Parcela 45 del término de Colomera"
    // Ej: "Ubicada en polígono 12 parcela 789 de Granada"
    const narrativeMatch = normalized.match(
      /(?:EN|DE|DEL)\s+POL[IÍ]?G?O?N?O?\s*(\d+)\s+(?:Y\s+)?PARC(?:ELA)?\s*(\d+)/i
    );
    if (narrativeMatch) {
      return {
        polygon: narrativeMatch[1].padStart(3, '0'),
        parcel: narrativeMatch[2].padStart(5, '0'),
        isRural: true
      };
    }

    // Patrón 5: Formato HÍBRIDO "P-X/P-Y" o "POL3/PAR45" 
    // Ej: "P-3/P-45 COLOMERA" o "POL3/PAR45"
    const hybridMatch = normalized.match(
      /P(?:OL)?[\s\-\.]*(\d+)\s*[\/\-]\s*P(?:A(?:R(?:C)?)?)?[\s\-\.]*(\d+)/i
    );
    if (hybridMatch) {
      return {
        polygon: hybridMatch[1].padStart(3, '0'),
        parcel: hybridMatch[2].padStart(5, '0'),
        isRural: true
      };
    }

    // Patrón 6: "P-X, Pa-Y" formato abreviado clásico
    const shortMatch = normalized.match(/P[\.\-]?\s*(\d+)[,\s]+PA[\.\-]?\s*(\d+)/i);
    if (shortMatch) {
      return {
        polygon: shortMatch[1].padStart(3, '0'),
        parcel: shortMatch[2].padStart(5, '0'),
        isRural: true
      };
    }

    // Patrón 7: Solo números de polígono/parcela en formato "3/45" o "3-45"
    // (más estricto: solo si es toda la cadena)
    const numericMatch = normalized.match(/^(\d{1,3})\s*[\/\-]\s*(\d{1,5})$/);
    if (numericMatch) {
      return {
        polygon: numericMatch[1].padStart(3, '0'),
        parcel: numericMatch[2].padStart(5, '0'),
        isRural: true
      };
    }

    // Patrón 8: Referencia catastral suelta en texto largo
    // Último recurso: buscar cualquier secuencia de 14 chars alfanuméricos
    // que parezca referencia catastral
    const looseRefMatch = normalized.match(/\b([0-9]{5}[A-Z][0-9]{3}[0-9A-Z]{5})\b/);
    if (looseRefMatch) {
      const ref = looseRefMatch[1];
      return {
        fullRef: ref,
        province: ref.substring(0, 2),
        municipality: ref.substring(2, 5),
        sector: ref.substring(5, 6),
        polygon: ref.substring(6, 9),
        parcel: ref.substring(9, 14),
        isRural: ref[5] === 'A'
      };
    }

    return null;
  }

  /**
   * Valida si una cadena es una referencia catastral válida
   */
  public isValidCadastralRef(ref: string): boolean {
    if (!ref || typeof ref !== 'string') return false;
    
    const normalized = ref.toUpperCase().trim();
    
    // Referencia de 14 caracteres (parcela)
    if (/^[0-9]{5}[A-Z0-9][0-9]{3}[0-9A-Z]{5}$/.test(normalized)) {
      return true;
    }
    
    // Referencia de 20 caracteres (inmueble completo)
    if (/^[0-9]{5}[A-Z0-9][0-9]{3}[0-9A-Z]{5}[0-9A-Z]{6}$/.test(normalized)) {
      return true;
    }

    return false;
  }

  /**
   * Limpia la caché
   */
  public clearCache(): void {
    this.cache.clear();
  }

  /**
   * Obtiene estadísticas del servicio
   */
  public getStats(): CatastroStats & { cacheSize: number; endpoint: string } {
    return {
      ...this.stats,
      cacheSize: this.cache.size,
      endpoint: this.COORD_ENDPOINT
    };
  }

  /**
   * Resetea estadísticas
   */
  public resetStats(): void {
    this.stats = {
      totalRequests: 0,
      successfulRequests: 0,
      failedRequests: 0,
      cacheHits: 0,
      avgResponseTime: 0
    };
  }
}

export default CatastroService;
